// Server-side MIRROR of the wizard's requirements matrix
// (frontend/src/lib/eventVocabularies.js: TREATMENT_REQUIREMENTS `uploads` +
// `fields`, the attachment-category labels and FIELD_META labels). notify /
// priority / optionalFields stay wizard concerns. KEEP IN SYNC when the matrix
// changes (same house pattern as DEFAULT_REQUIREMENT_TYPES mirroring
// AppConfigContext):
//   uploads  → MATRIX_UPLOADS + UPLOAD_LABELS   (matrixRequirements)
//   fields   → MATRIX_FIELDS  + FIELD_LABELS    (matrixFieldRequirements)
// 0.46: AWI requires product_used / area_treated / dosage / symptoms on every
// code; injector_notified_acknowledgement is optional (wizard optionalFields) so
// it is absent from MATRIX_FIELDS.
// METADATA on the training_certificate file (uploadMetadata.js), not as case
// fields — the wizard's _LHR_C3_BASE.fields is [] to match (FIELD_META keeps
// the two keys only so historic values still display).
//
// Purpose: matrix-driven requirements exist from the moment a case is created
// (derived from treatment · severity, never stored), so postCase surfaces them
// to requirement_added notification rules, the rule preview counts them, and
// outstanding.js derives the "still missing" list the reminder engine nags on.
//
// The universal "photo" baseline (every non-AWI case) is intentionally NOT
// mirrored: as a notification trigger it is indistinguishable from
// case_submitted and would fire on every single case.

const UPLOAD_LABELS = Object.freeze({
  training_certificate:           'Training certificate',
  consent_form:                   'Consent form',
  treating_therapist_statement:   'Treating therapist statement',
  pre_photo:                      'Pre photo',
  post_photo:                     'Post photo',
  dosage_chart:                   'Dosage chart',
  laser_log_files:                'Laser log files',
  injector_consultancy_agreement: 'Injector consultancy agreement',
  injector_insurance_policy:      'Injector insurance policy',
});

/** Required type_specific FIELD labels — mirror of FIELD_META[key].label
 *  (matrix-REQUIRED keys only; training_date / trainer_name moved to the
 *  training_certificate upload metadata in 0.46). */
const FIELD_LABELS = Object.freeze({
  product_used:        'Product used',
  area_treated:        'Area treated',
  dosage:              'Dosage',
  symptoms:            'Symptoms',
  client_skin_type:    'Client skin type',
  laser_settings:      'Laser settings used',
  laser_serial_number: 'Laser serial number',
  incident_datetime:   'Date and time of incident',
});

const LHR_C3 = Object.freeze(['training_certificate', 'consent_form', 'treating_therapist_statement']);
const LHR_C5 = Object.freeze([...LHR_C3, 'laser_log_files']);
const AWI_C3 = Object.freeze(['pre_photo', 'post_photo', 'consent_form', 'dosage_chart']);
const AWI_C4 = Object.freeze([...AWI_C3, 'injector_consultancy_agreement', 'injector_insurance_policy']);

const MATRIX_UPLOADS = Object.freeze({
  LHR: Object.freeze({ 'Code 3': LHR_C3, 'Code 4': LHR_C3, 'Code 5': LHR_C5 }),
  AWI: Object.freeze({ 'Code 3': AWI_C3, 'Code 4': AWI_C4, 'Code 5': AWI_C4 }),
});

// Required FIELDS (mirror of `fields`, never `optionalFields`). LHR Code 3/4
// require none (training date / trainer name ride on the certificate upload).
const LHR_F3 = Object.freeze([]);
const LHR_F5 = Object.freeze([...LHR_F3, 'client_skin_type', 'laser_settings', 'laser_serial_number', 'incident_datetime']);

const AWI_F = Object.freeze(['product_used', 'area_treated', 'dosage', 'symptoms']);

const MATRIX_FIELDS = Object.freeze({
  LHR: Object.freeze({ 'Code 3': LHR_F3, 'Code 4': LHR_F3, 'Code 5': LHR_F5 }),
  // AWI: product / area / dosage / symptoms are mandatory on every code
  // (product-owner decision 2026-09-09); the injector yes/no switch is optional
  // and therefore absent here.
  AWI: Object.freeze({ 'Code 1': AWI_F, 'Code 2': AWI_F, 'Code 3': AWI_F, 'Code 4': AWI_F, 'Code 5': AWI_F }),
});

/** Every field key the matrix can require (the rule builder's key picker). */
export const MATRIX_FIELD_KEYS = Object.freeze(Object.keys(FIELD_LABELS));

/** Resolve a stored department (canonical value or legacy alias, any case) to
 *  a matrix key via the treatmentTypes vocabulary; falls back to the
 *  uppercased raw value so canonical rows resolve with no vocab loaded. */
export function resolveTreatmentKey(department, treatmentTypes = null) {
  const raw = String(department || '').trim();
  if (!raw) return null;
  const up = raw.toUpperCase();
  for (const t of Array.isArray(treatmentTypes) ? treatmentTypes : []) {
    if (!t) continue;
    const value = String(t.value ?? t.key ?? '').trim();
    if (!value) continue;
    const aliases = Array.isArray(t.aliases) ? t.aliases : [];
    if (value.toUpperCase() === up || aliases.some(a => String(a || '').trim().toUpperCase() === up)) {
      return value;
    }
  }
  return up;
}

/** Matrix-driven UPLOAD requirements a new case is born with: [{ key, label }].
 *  Empty when the treatment · severity tuple demands nothing beyond the
 *  photo baseline (e.g. LHR Code 1, non-coded workbook treatments). */
export function matrixRequirements(department, severity, treatmentTypes = null) {
  const treatment = resolveTreatmentKey(department, treatmentTypes);
  const uploads = MATRIX_UPLOADS[treatment]?.[String(severity || '').trim()] || [];
  return uploads.map(u => ({ key: u, label: UPLOAD_LABELS[u] || u }));
}

/** Matrix-driven REQUIRED FIELD requirements (type_specific keys) for a
 *  treatment · severity tuple: [{ key, label }]. Required fields only — the
 *  wizard's optionalFields never appear here. Empty for AWI and every
 *  non-coded treatment. */
export function matrixFieldRequirements(department, severity, treatmentTypes = null) {
  const treatment = resolveTreatmentKey(department, treatmentTypes);
  const fields = MATRIX_FIELDS[treatment]?.[String(severity || '').trim()] || [];
  return fields.map(f => ({ key: f, label: FIELD_LABELS[f] || f }));
}
